import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CoreActivitySyllabus } from '../../../models/CoreActivity';
import { RequestState } from '../../../models/RequestState';
import { GetActivitySyllabus } from '../../../store/ActivityAction';
import StatusCode from '../../../util/StatusCode';
import TableVertical, { TableVerticalInfoObject } from '../../misc/TableVertical';
import * as Helper from '../../../util/Helper';
import * as SchoolTypeUtil from '../../../util/SchoolTypeUtil';
import * as CurriculumUtil from '../../../util/CurriculumUtil';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';



const ActivitySyllabusView: React.FC = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { activityId } = useParams();
    const dispatch = useDispatch();
    const syllabus = useSelector<RootState, RequestState<CoreActivitySyllabus>>(state => state.activities.activitySyllabus);

    useEffect(() => {
        if (activityId) {
            GetActivitySyllabus(activityId)(dispatch)
        }
    }, [activityId, dispatch]);

    const isError = syllabus.code === StatusCode.ERROR;
    const isPending = syllabus.code === StatusCode.PENDING;
    const syllabusInfo: TableVerticalInfoObject[] = [];

    if (syllabus.code === StatusCode.COMPLETE) {
        syllabusInfo.push(
            { header: t('commonTableHeaders.title', {ns: 'schoolAdministration'}), content: syllabus.data.specialisationCourseContent?.title },
            { header: t('syllabusTableHeaders.description', { ns: 'schoolAdministration' }), content: syllabus.data.specialisationCourseContent?.description },
            { header: t('syllabusTableHeaders.titleEnglish', { ns: 'schoolAdministration' }), content: syllabus.data.specialisationCourseContent?.titleEnglish },
            { header: t('syllabusTableHeaders.descriptionEnglish', { ns: 'schoolAdministration' }), content: syllabus.data.specialisationCourseContent?.descriptionEnglish },
            { header: t('syllabusTableHeaders.subjectCode', { ns: 'schoolAdministration' }), content: syllabus.data.subjectCode },
            { header: t('syllabusTableHeaders.subjectName', { ns: 'schoolAdministration' }), content: syllabus.data.subjectName },
            { header: t('syllabusTableHeaders.subjectDesignation', { ns: 'schoolAdministration' }), content: syllabus.data.subjectDesignation },
            { header: t('syllabusTableHeaders.courseCode', { ns: 'schoolAdministration' }), content: syllabus.data.courseCode },
            { header: t('syllabusTableHeaders.courseName', { ns: 'schoolAdministration' }), content: syllabus.data.courseName },
            { header: t('genericTableHeaders.id', { ns: 'common' }), content: syllabus.data.id },
            { header: t('genericTableHeaders.active', { ns: 'common' }), content: Helper.active(syllabus.data.active) },
            { header: t('syllabusTableHeaders.official', { ns: 'schoolAdministration' }), content: Helper.active(syllabus.data.official) },
            { header: t('syllabusTableHeaders.startSchoolYear', { ns: 'schoolAdministration' }), content: syllabus.data.startSchoolYear },
            { header: t('syllabusTableHeaders.endSchoolYear', { ns: 'schoolAdministration' }), content: syllabus.data.endSchoolYear },
            { header: t('syllabusTableHeaders.points', { ns: 'schoolAdministration' }), content: syllabus.data.points },
            { header: t('syllabusTableHeaders.languageCode', { ns: 'schoolAdministration' }), content: syllabus.data.languageCode },
            { header: t('commonTableHeaders.schoolType', { ns: 'schoolAdministration' }), content: SchoolTypeUtil.getTranslationKeys(syllabus.data.schoolType ?? 0).map(k => t(k, { ns: 'common' })).join(', ') },
            { header: t('syllabusTableHeaders.curriculum', { ns: 'schoolAdministration' }), content: CurriculumUtil.getName(syllabus.data.curriculum) },
            { header: t('genericTableHeaders.created', { ns: 'common' }), content: Helper.formatDate(syllabus.data.created) },
            { header: t('genericTableHeaders.modified', { ns: 'common' }), content: Helper.formatDate(syllabus.data.modified) },
            { header: t('genericTableHeaders.removed', { ns: 'common' }), content: Helper.formatDate(syllabus.data.removed) },
        )
    }

    return (
        <TableVertical
            header={t('syllabusTableHeaders.syllabus', {ns: 'schoolAdministration'})}
            info={syllabusInfo}
            error={isError}
            searching={isPending}
        />);
}

export default ActivitySyllabusView;
